import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DataCaptureToolType} from 'llm/data-capture/DataCaptureToolTypes';
import {useActivityModalControls} from 'llm/deep-research/ActivityModal';
import {
  DeepResearchProgressData,
  DeepResearchToolType,
  getToolIcon,
} from 'llm/deep-research/tool-details/types';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import Label from 'ui-library/bs/Label';
import {
  BLUE_DARKER,
  GRAY_DARK,
  GRAY_DARKER,
  NEAR_BLACK,
  PURPLE_LIGHTEST,
  RED_LIGHTEST,
  WHITE,
} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

import {ProgressData, ProgressToolCall} from '../progress/ProgressTypes';
import {ChatFailureIndicator} from './ChatFailureIndicator';
import {ClientProgressMessage, ProgressStatus} from './MessageTypes';
import {ProgressFailed as SharedProgressFailed} from './ProgressFailed';
import {ThinkingSpinner} from './ThinkingSpinner';
import {ToolActivityItem} from './ToolActivityItem';

const TEST_COMPONENT = 'ChatProgress';

// ============================================================================
// Shared Progress Components
// Used by both Deep Research and Data Capture agents
// ============================================================================

export interface ProgressMessageProps {
  message: ClientProgressMessage;
  progressData: ProgressData | null;
  onToolClick?: (toolCall: ProgressToolCall) => void;
}

/**
 * A unified progress component for agents that shows an expandable "Thinking" section
 * with tool calls and their outputs. Used by both Deep Research and Data Capture.
 */
export function ProgressMessage({message, progressData, onToolClick}: ProgressMessageProps): JSX.Element {
  const {status, errorMessage} = message;

  switch (status) {
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <div
          className={css(progressStyles.container)}
          {...getTestProps({
            component: TEST_COMPONENT,
            element: 'root',
          })}
        >
          <ThinkingSpinner text="Thinking" testComponent={TEST_COMPONENT} />
          <ProgressActivity progressData={progressData} status={status} onToolClick={onToolClick} />
          {errorMessage && (
            <div className={css(progressStyles.errorContent)}>
              <span className={css(agentProgressStyles.errorMessage)}>{errorMessage}</span>
            </div>
          )}
        </div>
      );
    case ProgressStatus.COMPLETED:
      // Completed state is now handled by ChatProgressMessageBubble using CompletedThinkingSection.
      // This case is kept as a fallback.
      if (progressData && progressData.historyThread.length > 0) {
        return <CompletedThinkingSection progressData={progressData} onToolClick={onToolClick} />;
      }
      return <></>;
    case ProgressStatus.FAILED:
      return (
        <SharedProgressFailed
          title="Request failed"
          errorMessage={errorMessage || 'Could not complete request'}
          testComponent={TEST_COMPONENT}
        />
      );
    default:
      throw new Error(`Unknown progress status: ${assertNever(status)}`);
  }
}

interface ProgressActivityProps {
  progressData: ProgressData | null;
  status: ProgressStatus;
  onToolClick?: (toolCall: ProgressToolCall) => void;
}

function ProgressActivity({progressData, status, onToolClick}: ProgressActivityProps): JSX.Element {
  const hasToolCalls = progressData && progressData.historyThread.length > 0;

  return (
    <div
      className={css(progressStyles.activityContent)}
      {...getTestProps({
        component: TEST_COMPONENT,
        element: 'content',
      })}
    >
      {status === ProgressStatus.STARTED || !hasToolCalls ? (
        <Label className={css(progressStyles.analyzingText)}>Working on your request...</Label>
      ) : onToolClick ? (
        // Deep Research style: clickable items with icons, but show Think tool content
        progressData.historyThread.map((toolCall, index) => (
          <DeepResearchToolItem
            key={toolCall.toolId || index}
            toolCall={toolCall}
            onClick={() => {
              onToolClick(toolCall);
            }}
            testKey={`${index}`}
          />
        ))
      ) : (
        // Data Capture style: tool items with detail text
        progressData.historyThread.map((toolCall, index) => (
          <ToolCallItem key={toolCall.toolId || index} toolCall={toolCall} />
        ))
      )}
    </div>
  );
}

interface ThinkingContentProps {
  progressData: ProgressData;
  isExpanded: boolean;
  onToolClick?: (toolCall: ProgressToolCall) => void;
}

function ThinkingContent({progressData, isExpanded, onToolClick}: ThinkingContentProps): JSX.Element | null {
  if (!isExpanded) {
    return null;
  }

  return (
    <div
      className={css(progressStyles.expandedContent)}
      {...getTestProps({
        component: TEST_COMPONENT,
        element: 'content',
      })}
    >
      {onToolClick
        ? // Deep Research style: clickable items with icons, but show Think tool content
          progressData.historyThread.map((toolCall, index) => (
            <DeepResearchToolItem
              key={toolCall.toolId || index}
              toolCall={toolCall}
              onClick={() => {
                onToolClick(toolCall);
              }}
              testKey={`${index}`}
            />
          ))
        : // Data Capture style: tool items with detail text
          progressData.historyThread.map((toolCall, index) => (
            <ToolCallItem key={toolCall.toolId || index} toolCall={toolCall} />
          ))}
    </div>
  );
}

interface ToolCallItemProps {
  toolCall: ProgressToolCall;
}

/**
 * Get the display text for a tool call's input, if it should be shown.
 * Returns null if the tool type should not display input.
 * Only Data Capture tools have input display - Deep Research tools return null.
 */
function getToolInputDisplay(toolCall: ProgressToolCall): string | null {
  const {tool, args} = toolCall;
  if (!args) {
    return null;
  }

  switch (tool) {
    case DataCaptureToolType.FillTables:
      // Show agent_instructions for FillTables
      if (typeof args.agent_instructions === 'string') {
        return args.agent_instructions;
      }
      return null;

    case DataCaptureToolType.SearchSchemas:
      // Show search_queries for SearchSchemas
      if (Array.isArray(args.search_queries)) {
        return args.search_queries.join(', ');
      }
      return null;

    case DataCaptureToolType.AskQuestion:
      // Show question for AskQuestion
      if (typeof args.question === 'string') {
        return args.question;
      }
      return null;

    default:
      // Deep Research tools and other tools don't show input
      return null;
  }
}

/**
 * Get the display text for a tool call's output, if it should be shown.
 * Returns null if the tool type should not display output.
 * Only Data Capture tools have output display - Deep Research tools return null.
 */
function getToolOutputDisplay(toolCall: ProgressToolCall): string | null {
  const {tool, responseText} = toolCall;
  if (!responseText) {
    return null;
  }

  switch (tool) {
    case DataCaptureToolType.FillTables:
      // Show full output for FillTables
      return responseText;

    case DataCaptureToolType.SearchSchemas: {
      // Parse output to show count of schemas found
      const schemaMatch = responseText.match(/Found (\d+) (?:entity )?schema/i);
      if (schemaMatch) {
        return `Found ${schemaMatch[1]} schema${schemaMatch[1] === '1' ? '' : 's'}`;
      }
      // Fallback: try to count schema names in the response
      const schemaCount = (responseText.match(/Schema Name:/g) || []).length;
      if (schemaCount > 0) {
        return `Found ${schemaCount} schema${schemaCount === 1 ? '' : 's'}`;
      }
      return null;
    }

    case DataCaptureToolType.Think:
      // Show full output for Think
      return responseText;

    case DataCaptureToolType.AskQuestion:
      // AskQuestion responses are not displayed in the output section because
      // they contain internal search results that are not meaningful to show
      // in the progress UI. The agent uses these results internally.
      return null;

    default:
      // Other tools don't show output
      return null;
  }
}

function ToolCallItem({toolCall}: ToolCallItemProps): JSX.Element {
  const displayName = toolCall.displayName;
  const inputDisplay = getToolInputDisplay(toolCall);
  const outputDisplay = getToolOutputDisplay(toolCall);

  return (
    <div
      className={css(progressStyles.toolItem)}
      {...getTestProps({
        component: TEST_COMPONENT,
        element: 'toolItem',
      })}
    >
      <Label
        className={css(progressStyles.toolName)}
        {...getTestProps({
          component: TEST_COMPONENT,
          element: 'toolName',
        })}
      >
        {displayName}
      </Label>
      {inputDisplay && (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({
            component: TEST_COMPONENT,
            element: 'toolInput',
          })}
        >
          <ToolDetailContent text={inputDisplay} />
        </div>
      )}
      {outputDisplay && (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({
            component: TEST_COMPONENT,
            element: 'toolOutput',
          })}
        >
          <ToolDetailContent text={outputDisplay} />
        </div>
      )}
    </div>
  );
}

interface DeepResearchToolItemProps {
  toolCall: ProgressToolCall;
  onClick: () => void;
  testKey?: string;
}

/**
 * Deep Research tool item that shows icon + name, with expanded content for Think tool.
 * For Think tools, shows the thought content (responseText) below the header.
 * For other tools, shows just the icon and name (clickable to open activity modal).
 */
function DeepResearchToolItem({toolCall, onClick, testKey}: DeepResearchToolItemProps): JSX.Element {
  const isThinkTool = toolCall.tool === DeepResearchToolType.Think;
  const thinkContent = isThinkTool ? toolCall.responseText : null;

  return (
    <div
      className={css(progressStyles.toolItem)}
      {...getTestProps({component: TEST_COMPONENT, element: 'toolItem', key: testKey})}
    >
      <ToolActivityItem
        icon={getToolIcon(toolCall.tool)}
        displayName={toolCall.displayName}
        onClick={onClick}
        testComponent={TEST_COMPONENT}
        testKey={testKey}
      />
      {thinkContent && (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({
            component: TEST_COMPONENT,
            element: 'toolOutput',
            key: testKey,
          })}
        >
          <ToolDetailContent text={thinkContent} />
        </div>
      )}
    </div>
  );
}

interface ToolDetailContentProps {
  text: string;
}

const MAX_DETAIL_LENGTH = 500;

function ToolDetailContent({text}: ToolDetailContentProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > MAX_DETAIL_LENGTH;
  const displayText = shouldTruncate && !isExpanded ? `${text.slice(0, MAX_DETAIL_LENGTH)}...` : text;

  return (
    <div className={css(progressStyles.detailContentWrapper)}>
      <div className={css(progressStyles.markdownContent)}>
        <LLMMarkdownSection markdown={displayText} />
      </div>
      {shouldTruncate && (
        <button
          type="button"
          className={css(progressStyles.expandButton)}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export interface CompletedThinkingSectionProps {
  progressData: ProgressData;
  onToolClick?: (toolCall: ProgressToolCall) => void;
}

export function CompletedThinkingSection({
  progressData,
  onToolClick,
}: CompletedThinkingSectionProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const toolCount = progressData.historyThread.length;

  return (
    <div
      className={css(progressStyles.completedContainer)}
      {...getTestProps({component: TEST_COMPONENT, element: 'root'})}
    >
      <button
        type="button"
        className={css(progressStyles.completedHeader)}
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        {...getTestProps({component: TEST_COMPONENT, element: 'expandButton'})}
      >
        <BenchlingIcon
          icon={isExpanded ? 'caret-down' : 'caret-right'}
          className={css(progressStyles.expandIcon)}
        />
        <Label className={css(progressStyles.completedText)}>
          Thinking
          {toolCount > 0 ? ` • ${toolCount} ${toolCount === 1 ? 'step' : 'steps'}` : ''}
        </Label>
      </button>
      <ThinkingContent progressData={progressData} isExpanded={isExpanded} onToolClick={onToolClick} />
    </div>
  );
}

// ============================================================================
// Chat Progress Message Bubble
// Entry point for rendering progress messages in the chat
// ============================================================================

/**
 * Render a ClientProgressMessage taking status into account.
 *
 * If the message indicates a progress status, show a loading message.
 * If the message indicates a completed status, show nothing.
 * If the message indicates a failed status, show a failure indicator.
 */
export function ActiveChatProgressMessageBubble({
  message,
  inProgressText = 'Processing your request...',
}: {
  message: ClientProgressMessage;
  inProgressText?: string | Array<string>;
}): JSX.Element | null {
  switch (message.status) {
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <div className={css(agentProgressStyles.progressContainer)}>
          <LLMLoadingMessage text={inProgressText} fontSize={14} />
        </div>
      );
    case ProgressStatus.COMPLETED:
      return null;
    case ProgressStatus.FAILED:
      return <ChatFailureIndicator errorMessage={message.errorMessage} />;
    default:
      assertNever(message.status);
      return null;
  }
}

/**
 * Render the appropriate progress message bubble based on the agent type.
 */
export function ChatProgressMessageBubble({
  message,
  progressData,
}: {
  message: ClientProgressMessage;
  progressData: ProgressData | null;
}): JSX.Element {
  const {openActivityModal} = useActivityModalControls();

  // For active states (STARTED/IN_PROGRESS), only use message's progressData to show
  // current exchange tool calls. Don't fall back to progressData prop which contains ALL tool calls.
  // For COMPLETED status, we can fall back to progressData prop since it's used for completed sections
  // that may need access to all tool calls.
  const effectiveProgressData =
    message.status === ProgressStatus.COMPLETED
      ? (message.progressData ?? progressData ?? null)
      : (message.progressData ?? null);

  // Create click handler for Deep Research tool calls that opens the activity modal
  // Cast is safe: this handler is only used for Deep Research, which parses tool calls
  // using getDeepResearchToolCall, producing properly typed DeepResearchProgressToolCall objects
  const handleDeepResearchToolClick = (toolCall: ProgressToolCall): void => {
    if (effectiveProgressData) {
      openActivityModal(effectiveProgressData as DeepResearchProgressData, toolCall.toolId);
    }
  };

  // Handle COMPLETED status for agents with thinking sections
  if (
    message.status === ProgressStatus.COMPLETED &&
    (message.agentType === AgentType.DATA_CAPTURE || message.agentType === AgentType.DEEP_RESEARCH) &&
    effectiveProgressData &&
    effectiveProgressData.historyThread.length > 0
  ) {
    const onToolClick =
      message.agentType === AgentType.DEEP_RESEARCH ? handleDeepResearchToolClick : undefined;
    return <CompletedThinkingSection progressData={effectiveProgressData} onToolClick={onToolClick} />;
  }

  switch (message.agentType) {
    case AgentType.DEEP_RESEARCH:
      return (
        <ProgressMessage
          message={message}
          progressData={effectiveProgressData}
          onToolClick={handleDeepResearchToolClick}
        />
      );
    case AgentType.DATA_CAPTURE:
      return <ProgressMessage message={message} progressData={effectiveProgressData} />;
    case AgentType.ASK:
      return (
        <ActiveChatProgressMessageBubble
          message={message}
          inProgressText={[
            'Processing your request...',
            'Analyzing your question...',
            'Gathering information...',
            'Generating answer...',
          ]}
        />
      );
    case null:
      return <ActiveChatProgressMessageBubble message={message} />;
    default:
      assertNever(message.agentType);
      return <ActiveChatProgressMessageBubble message={message} />;
  }
}

// ============================================================================
// Styles
// ============================================================================

const TEXT_COLOR = NEAR_BLACK;
const BORDER_COLOR = GRAY_DARK;

/**
 * Styles specific to the unified progress components.
 */
const progressStyles = StyleSheet.create({
  // Container styles - white background
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: WHITE,
  },

  completedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '12px 0',
    gap: '0',
    backgroundColor: WHITE,
  },

  completedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },

  completedText: {
    color: TEXT_COLOR,
    margin: 0,
  },

  expandIcon: {
    color: TEXT_COLOR,
    fontSize: '12px',
  },

  // Activity content
  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  analyzingText: {
    color: TEXT_COLOR,
    margin: 0,
  },

  errorContent: {
    marginTop: '8px',
  },

  // Expanded content (tool list) with vertical line
  expandedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    marginLeft: '3px',
    paddingLeft: '16px',
    borderLeft: `2px solid ${BORDER_COLOR}`,
    background: 'transparent',
  },

  // Tool item styles
  toolItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    background: 'transparent',
  },

  toolName: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
    background: 'transparent',
    margin: 0,
  },

  toolDetailText: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    marginTop: '4px',
    marginLeft: '16px',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },

  detailContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },

  markdownContent: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    background: 'transparent',
    lineHeight: '1.5',
    wordBreak: 'break-word',
    // Override markdown section default styles
    '& section': {
      background: 'transparent',
    },
    '& p': {
      margin: '0.5em 0',
    },
    '& ul, & ol': {
      margin: '0.5em 0',
      paddingLeft: '1.5em',
    },
    '& li': {
      margin: '0.25em 0',
    },
    '& code': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '0.9em',
    },
    '& pre': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '8px',
      borderRadius: '4px',
      overflow: 'auto',
      margin: '0.5em 0',
    },
    '& pre code': {
      background: 'transparent',
      padding: 0,
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: '0.5em 0',
    },
    '& th, & td': {
      border: '1px solid rgba(0, 0, 0, 0.1)',
      padding: '4px 8px',
    },
    '& th': {
      background: 'rgba(0, 0, 0, 0.05)',
    },
  },

  expandButton: {
    ...FONT_PROXIMA_12,
    color: BLUE_DARKER,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    marginTop: '4px',
    textAlign: 'left',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

/**
 * Shared styles for agent progress components.
 * Used by deep research and data capture progress UIs.
 */
export const agentProgressStyles = StyleSheet.create({
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  completedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  progressCompletedContainer: {
    cursor: 'pointer',
    padding: '16px',
    borderRadius: '4px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  failedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '4px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    backgroundColor: RED_LIGHTEST,
  },

  progressHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '8px',
  },

  thinkingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
  },

  thinkingText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  headerIcon: {
    color: GRAY_DARK,
  },

  sourcesText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARK,
  },

  errorMessage: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },

  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: GRAY_DARK,
  },

  activityItemClickable: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    borderRadius: '4px',
  },

  activityIcon: {
    width: '16px',
    textAlign: 'center',
  },

  activityDescription: {
    ...FONT_PROXIMA_14,
    fontSize: '13px',
    color: GRAY_DARKER,
    flex: 1,
  },

  toolDescription: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },

  emptyActivityItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  completedSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },

  completedText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: GRAY_DARKER,
  },

  completedHeader: {
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },

  completedHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },

  expandIcon: {
    color: GRAY_DARK,
    fontSize: '12px',
  },
});
